import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { sendOtp, registerUser } from '../api/api';
import img from "../assets/profile.png";
import eventBus from "../eventBus";

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Only User registration now - technician removed
  const preFilledMobile = location.state?.mobile || '';

  // ---- Step state: 1 = details, 2 = OTP ----
  const [step, setStep] = useState(1);

  // ---- Form fields ----
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState(preFilledMobile);
  const [otp, setOtp] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipCode, setZipCode] = useState('');

  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState(img);

  // ---- Messages ----
  const [statusMessage, setStatusMessage] = useState('');
  const [apiError, setApiError] = useState('');
  const [errors, setErrors] = useState({});

  // ---- Profile image handling ----
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ---- Validate Step 1 (details) ----
  const validateStep1 = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Valid email is required';
    if (!mobile.trim() || !/^\d{10}$/.test(mobile)) newErrors.mobile = 'Valid 10‑digit mobile is required';
    if (!address.trim()) newErrors.address = 'Address is required';
    if (!city.trim()) newErrors.city = 'City is required';
    if (!zipCode.trim() || !/^\d{6}$/.test(zipCode)) newErrors.zipCode = '6‑digit zip code is required';
    if (!profileFile) newErrors.profileImage = 'Profile image is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- Handle Continue (Step 1 -> Step 2) ----
  const handleContinue = async () => {
    if (!validateStep1()) return;

    try {
      setApiError('');
      setStatusMessage('Sending OTP...');
      const response = await sendOtp(mobile, 'User');
      alert("your OTP is: " + response.otp);
      setStatusMessage('OTP sent to your mobile. Please enter it below.');
      setStep(2);
    } catch (error) {
      setApiError(error.message || 'Failed to send OTP.');
      setStatusMessage('');
    }
  };

  // ---- Handle Final Registration ----
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(otp)) {
      setApiError('Please enter the 4‑digit OTP.');
      return;
    }

    const payload = {
      name: fullName,
      email,
      mobile,
      otp,
      device_token: 'device_token_123',
      profile_pic: profileFile,
      addresses: JSON.stringify([
        {
          building_number: address,
          block: '',
          land_mark: '',
          city,
          pincode: Number(zipCode),
          location: { type: 'Point', coordinates: [0, 0] }
        }
      ])
    };

    try {
      setApiError('');
      setStatusMessage('Registering...');
      const response = await registerUser(payload);
      console.log('Register response:', response);

      if (response.status === 200 && response.data) {
        const token = response.data?.user?.token;
        if (token) {
          localStorage.removeItem('technician_token');
          localStorage.removeItem('technician_info');
          localStorage.removeItem('auth_token');
          const userInfo = {
            user_id: response.data.user?.user_id,
            user_name: response.data.user?.user_name || response.data.user?.name,
            mobile: response.data.user?.mobile,
            user_type: 'User',
          };
              eventBus.emit('userLoggedIn', { 
                isLoggedIn: true, 
                userInfo, 
                userType: "user" 
              });
          console.log('Storing auth_token and user_info in localStorage:', token, userInfo);
          localStorage.setItem('user_token', token);
          localStorage.setItem('user_info', JSON.stringify(userInfo));
        }

        setStatusMessage('Registration successful! Redirecting...');
        setTimeout(() => navigate('/profile'), 500);
      } else {
        setApiError('Registration failed. Please try again with correct data.');
        setStatusMessage('');
      }
    } catch (error) {
      setApiError(error.message || 'Network error while registering.');
      setStatusMessage('');
    }
  };

  // ---- Resend OTP ----
  const handleResendOtp = async () => {
    try {
      setStatusMessage('Resending OTP...');
      const response = await sendOtp(mobile, 'User');
      alert("your OTP is: " + response.otp);
      setStatusMessage('OTP resent successfully.');
    } catch (error) {
      setApiError(error.message || 'Failed to resend OTP.');
    }
  };

  return (
    <div className="regflowContainer">
      <div className="regflowWrapper">
        {/* Left Side - Branding/Design Section */}
        <div className="regflowLeftPanel">
          <div className="regflowBrandContent">
            <div className="regflowLogoBadge">
              <span className="regflowLogoIcon">✨</span>
              <span>Join Us Today</span>
            </div>

            <h1 className="regflowMainTitle">
              Create Your<br />
              <span className="regflowGradientText">Account</span>
            </h1>

            <p className="regflowDescription">
              Join our platform and experience seamless service booking,
              professional support, and exclusive benefits tailored just for you.
            </p>

            <div className="regflowFeatureGrid">
              <div className="regflowFeatureItem">
                <div className="regflowFeatureIcon">🎯</div>
                <div className="regflowFeatureText">
                  <strong>Easy Registration</strong>
                  <p>Simple 2-step verification</p>
                </div>
              </div>

              <div className="regflowFeatureItem">
                <div className="regflowFeatureIcon">⚡</div>
                <div className="regflowFeatureText">
                  <strong>Quick Access</strong>
                  <p>Instant account activation</p>
                </div>
              </div>

              <div className="regflowFeatureItem">
                <div className="regflowFeatureIcon">🔒</div>
                <div className="regflowFeatureText">
                  <strong>Secure Platform</strong>
                  <p>Your data is protected</p>
                </div>
              </div>
            </div>
          </div>

          <div className="regflowDecorationCircle"></div>
          <div className="regflowDecorationCircle2"></div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="regflowRightPanel">
          <div className="regflowFormContainer">
            <h2 className="regflowFormTitle">
              {step === 1 ? 'Complete Your Registration' : 'Verify Your Account'}
            </h2>

            <p className="regflowFormSubtitle">
              {step === 1
                ? 'Fill in your details to create your account'
                : `We've sent a verification code to ${mobile.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`}
            </p>

            {/* Step 1: User Details */}
            {step === 1 && (
              <form className="regflowForm" onSubmit={(e) => e.preventDefault()}>
                {/* Profile Image Upload */}
                <div className="regflowProfileSection">
                  <div className="regflowProfileUpload">
                    <input
                      type="file"
                      id="regflowProfileImage"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="regflowFileInput"
                    />
                    <label htmlFor="regflowProfileImage" className="regflowProfileLabel">
                      <img src={profilePreview} alt="Profile" className="regflowProfilePreview" />
                      <div className="regflowProfileOverlay">
                        <span>📸</span>
                      </div>
                    </label>
                  </div>
                  <p className="regflowProfileText">Upload Profile Image</p>
                  {errors.profileImage && <span className="regflowErrorMsg">{errors.profileImage}</span>}
                </div>

                <div className="regflowInputWrapper">
                  <input
                    type="text"
                    className="regflowInput"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder=" "
                  />
                  <label className="regflowLabel">Full Name <span className="regflowRequired">*</span></label>
                  {errors.fullName && <span className="regflowErrorMsg">{errors.fullName}</span>}
                </div>

                <div className="regflowInputWrapper">
                  <input
                    type="email"
                    className="regflowInput"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=" "
                  />
                  <label className="regflowLabel">Email Address <span className="regflowRequired">*</span></label>
                  {errors.email && <span className="regflowErrorMsg">{errors.email}</span>}
                </div>

                <div className="regflowInputWrapper">
                  <input
                    type="tel"
                    className="regflowInput"
                    maxLength="10"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder=" "
                    disabled
                  />
                  <label className="regflowLabel">Phone Number <span className="regflowRequired">*</span></label>
                  {errors.mobile && <span className="regflowErrorMsg">{errors.mobile}</span>}
                </div>

                <div className="regflowInputWrapper">
                  <input
                    type="text"
                    className="regflowInput"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder=" "
                  />
                  <label className="regflowLabel">Address <span className="regflowRequired">*</span></label>
                  {errors.address && <span className="regflowErrorMsg">{errors.address}</span>}
                </div>

                <div className="regflowRow">
                  <div className="regflowInputWrapper">
                    <input
                      type="text"
                      className="regflowInput"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder=" "
                    />
                    <label className="regflowLabel">City <span className="regflowRequired">*</span></label>
                    {errors.city && <span className="regflowErrorMsg">{errors.city}</span>}
                  </div>

                  <div className="regflowInputWrapper">
                    <input
                      type="text"
                      className="regflowInput"
                      maxLength="6"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      placeholder=" "
                    />
                    <label className="regflowLabel">Zip Code <span className="regflowRequired">*</span></label>
                    {errors.zipCode && <span className="regflowErrorMsg">{errors.zipCode}</span>}
                  </div>
                </div>

                <button type="button" className="regflowPrimaryBtn" onClick={handleContinue}>
                  Continue
                </button>

                {apiError && <div className="regflowErrorMsg">{apiError}</div>}
                {statusMessage && <div className="regflowStatusMsg">{statusMessage}</div>}
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <form className="regflowForm" onSubmit={handleRegister}>
                <div className="regflowOtpSection">
                  <div className="regflowInputWrapper">
                    <input
                      type="text"
                      maxLength="4"
                      className="regflowInput regflowOtpInput"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder=" "
                    />
                    <label className="regflowLabel">Enter OTP <span className="regflowRequired">*</span></label>
                  </div>

                  <button type="submit" className="regflowPrimaryBtn">
                    Register Account
                  </button>

                  <button
                    type="button"
                    className="regflowSecondaryBtn"
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </button>
                </div>

                {apiError && <div className="regflowErrorMsg">{apiError}</div>}
                {statusMessage && <div className="regflowStatusMsg">{statusMessage}</div>}
              </form>
            )}

            <div className="regflowLoginLink">
              <p>Already have an account? <Link to="/login">Sign In</Link></p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Unique classname styling for registration page */
        .regflowContainer {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }

        .regflowWrapper {
          max-width: 1280px;
          width: 100%;
          background: #FFFFFF;
          border-radius: 32px;
          display: flex;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          animation: regflowFadeIn 0.5s ease-out;
        }

        @keyframes regflowFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Left Panel */
        .regflowLeftPanel {
          flex: 1;
          background: #FFFFFF;
          padding: 48px 40px;
          position: relative;
          overflow: hidden;
          border-right: 1px solid #f0f0f0;
        }

        .regflowBrandContent {
          position: relative;
          z-index: 2;
        }

        .regflowLogoBadge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #f8f8f8;
          padding: 8px 16px;
          border-radius: 40px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #000;
          margin-bottom: 48px;
          border: 1px solid #eaeaea;
        }

        .regflowLogoIcon {
          font-size: 1.2rem;
        }

        .regflowMainTitle {
          font-size: 3rem;
          font-weight: 700;
          line-height: 1.2;
          color: #000;
          margin-bottom: 20px;
          letter-spacing: -0.02em;
        }

        .regflowGradientText {
          background: linear-gradient(135deg, #000000 0%, #333333 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .regflowDescription {
          color: #666;
          line-height: 1.6;
          margin-bottom: 40px;
          font-size: 0.95rem;
        }

        .regflowFeatureGrid {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .regflowFeatureItem {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .regflowFeatureIcon {
          width: 48px;
          height: 48px;
          background: #fafafa;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          border: 1px solid #f0f0f0;
        }

        .regflowFeatureText strong {
          display: block;
          color: #000;
          margin-bottom: 4px;
          font-size: 0.95rem;
        }

        .regflowFeatureText p {
          color: #888;
          font-size: 0.8rem;
          margin: 0;
        }

        .regflowDecorationCircle {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 70%);
          bottom: -100px;
          right: -100px;
          animation: regflowPulse 8s ease-in-out infinite;
        }

        .regflowDecorationCircle2 {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0) 70%);
          top: -50px;
          left: -50px;
          animation: regflowPulse 6s ease-in-out infinite reverse;
        }

        @keyframes regflowPulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        /* Right Panel */
        .regflowRightPanel {
          flex: 1;
          padding: 48px 40px;
          background: #FFFFFF;
          display: flex;
          flex-direction: column;
        }

        .regflowFormContainer {
          width: 100%;
        }

        .regflowFormTitle {
          font-size: 1.75rem;
          font-weight: 700;
          color: #000;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .regflowFormSubtitle {
          color: #888;
          font-size: 0.9rem;
          margin-bottom: 32px;
          line-height: 1.5;
        }

        /* Form Elements */
        .regflowForm {
          animation: regflowSlideIn 0.4s ease-out;
        }

        @keyframes regflowSlideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .regflowInputWrapper {
          position: relative;
          margin-bottom: 28px;
        }

        .regflowInput {
          width: 100%;
          padding: 16px 16px 8px 16px;
          font-size: 1rem;
          border: 2px solid #e8e8e8;
          border-radius: 16px;
          background: #FFFFFF;
          transition: all 0.3s ease;
          outline: none;
          color: #000;
          font-weight: 500;
        }

        .regflowInput:focus {
          border-color: #000;
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
        }

        .regflowInput:focus + .regflowLabel,
        .regflowInput:not(:placeholder-shown) + .regflowLabel {
          transform: translateY(-20px) scale(0.85);
          color: #000;
        }

        .regflowLabel {
          position: absolute;
          left: 16px;
          top: 10%;
          transform: translateY(-50%);
          background: #FFFFFF;
          padding: 0 4px;
          transition: all 0.2s ease;
          pointer-events: none;
          color: #999;
          font-size: 1rem;
        }

        .regflowRequired {
          color: #ff0000;
          margin-left: 2px;
        }

        .regflowRow {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        /* Profile Upload */
        .regflowProfileSection {
          text-align: center;
          margin-bottom: 32px;
        }

        .regflowProfileUpload {
          position: relative;
          display: inline-block;
        }

        .regflowFileInput {
          display: none;
        }

        .regflowProfileLabel {
          cursor: pointer;
          display: block;
        }

        .regflowProfilePreview {
          width: 120px;
          height: 120px;
          border-radius: 60px;
          object-fit: cover;
          border: 3px solid #e8e8e8;
          transition: all 0.3s ease;
        }

        .regflowProfileOverlay {
          position: absolute;
          bottom: 0;
          right: 0;
          background: #000;
          border-radius: 30px;
          padding: 6px;
          color: white;
          font-size: 1rem;
        }

        .regflowProfileText {
          margin-top: 12px;
          color: #666;
          font-size: 0.85rem;
        }

        /* Buttons */
        .regflowPrimaryBtn {
          width: 100%;
          background: #000000;
          color: #FFFFFF;
          border: none;
          padding: 16px 24px;
          border-radius: 16px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 8px;
        }

        .regflowPrimaryBtn:hover {
          background: #222;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .regflowSecondaryBtn {
          width: 100%;
          background: transparent;
          color: #000;
          border: 2px solid #e8e8e8;
          padding: 14px 24px;
          border-radius: 16px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 12px;
        }

        .regflowSecondaryBtn:hover {
          border-color: #000;
          background: #fafafa;
        }

        /* Messages */
        .regflowErrorMsg {
          margin-top: 8px;
          padding: 8px 12px;
          background: #fff5f5;
          border-left: 4px solid #ff0000;
          color: #cc0000;
          border-radius: 8px;
          font-size: 0.85rem;
          animation: regflowShake 0.3s ease;
        }

        .regflowStatusMsg {
          margin-top: 16px;
          padding: 12px;
          background: #f0f9ff;
          border-left: 4px solid #000;
          color: #000;
          border-radius: 8px;
          font-size: 0.9rem;
          animation: regflowFadeIn 0.3s ease;
        }

        @keyframes regflowShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        /* Login Link */
        .regflowLoginLink {
          text-align: center;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #f0f0f0;
        }

        .regflowLoginLink p {
          color: #666;
          font-size: 0.9rem;
        }

        .regflowLoginLink a {
          color: #000;
          text-decoration: none;
          font-weight: 600;
          margin-left: 5px;
          transition: color 0.2s ease;
        }

        .regflowLoginLink a:hover {
          text-decoration: underline;
        }

        /* OTP Specific */
        .regflowOtpSection {
          margin-top: 20px;
        }

        .regflowOtpInput {
          text-align: center;
          font-size: 1.5rem;
          letter-spacing: 8px;
          font-weight: 600;
        }

        /* Responsive */
        @media (max-width: 968px) {
          .regflowWrapper {
            flex-direction: column;
            max-width: 550px;
          }
          
          .regflowLeftPanel {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
            padding: 32px 28px;
          }
          
          .regflowRightPanel {
            padding: 32px 28px;
          }
          
          .regflowMainTitle {
            font-size: 2rem;
          }
          
          .regflowRow {
            grid-template-columns: 1fr;
            gap: 0;
          }
        }

        @media (max-width: 480px) {
          .regflowProfilePreview {
            width: 100px;
            height: 100px;
          }
          
          .regflowFormTitle {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Register;